// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
/**
 * Slim useAuth shim — replaces the original Redux-driven hook that
 * owned login / 2FA / logout flows.
 *
 * Auth is now owned by the dashboard. CrmRoot bootstraps a brain
 * crm-api session at /crm entry and dispatches `setAuthData`. This
 * hook exposes a read-only view of that state.
 *
 * ── PORTAL DIVERGENCE — the source's `logout()` is NOT here. ──
 * The source ships a `logout()` for one caller, `components/crm-layout.tsx`,
 * the command center's CRM shell. That shell is not ported: this portal has
 * its own, and its own sign-out — `auth/AuthProvider.tsx`'s `logout`, reached
 * from `pages/portal/ProfileMenu.tsx`. Keeping the source's copy here left a
 * second sign-out that nothing called and that was wrong twice if anything
 * ever had: it sent DELETE where this app's BFF answers POST
 * (`apps/server/src/routes/auth.ts`), and it finished with
 * `window.location.assign("/login")` — the full page load removed from
 * `lib/api.ts`, which discards the router and with it the reseller's route.
 * Read-only is the whole contract of this shim here; sign-out belongs to the
 * app that owns the session. See src/crm/README.md.
 */
import { useAppSelector } from "@crm-src/lib/store";

import { type Tenant, type User } from "@crm-src/lib/auth";

export interface UseAuthValue {
  user: User | null;
  tenant: Tenant | null;
  permissions: string[];
  isAuthenticated: boolean;
  isLoading: boolean;
}

export function useAuth(): UseAuthValue {
  const user = useAppSelector((state) => state.auth.user) as User | null;
  const tenant = useAppSelector((state) => state.auth.tenant) as Tenant | null;
  const permissions = useAppSelector((state) => state.auth.permissions) as string[];
  const isLoading = useAppSelector((state) => state.auth.isLoading) as boolean;

  return {
    user,
    tenant,
    permissions,
    isAuthenticated: Boolean(user),
    isLoading,
  };
}

export default useAuth;
